import type { Translation } from './en';

/**
 * Bulgarian strings. Reviewed for terminology and grammar in M8 (path → „път“, rate → „ставка“,
 * cash balance → „наличност“); a native speaker's pass before launch is still recommended.
 */
export const bg: Translation = {
  app: {
    name: 'Kasora',
    untitledProject: 'Проект без име',
    projectMenu: 'Меню на проекта',
    projects: 'Проекти',
    renameProject: 'Преименуване на проекта',
  },
  views: {
    label: 'Изгледи',
    overview: 'Обзор',
    cashFlow: 'Паричен поток',
    balance: 'Баланс',
    debt: 'Дългове',
    risk: 'Риск',
    compare: 'Сравнение',
    ledger: 'Регистър',
    returns: 'Възвръщаемост',
  },
  valueMode: {
    label: 'Стойности',
    nominal: 'Номинални',
    real: 'Реални',
    nominalHint: 'Суми такива, каквито ще бъдат в съответната година',
    realHint: 'Суми в днешни пари, коригирани с инфлацията',
  },
  simMode: {
    label: 'Режим на симулация',
    deterministic: 'Детерминистичен',
    monteCarlo: 'Монте Карло',
    deterministicHint: 'Един път, при който всички ставки са на средните си стойности',
    monteCarloHint: 'Много случайни пътища, показани като диапазони от резултати',
  },
  variants: {
    dropShare: 'Използване и в {{name}}',
    dropCopy: 'Копиране в „{{name}}“',
    title: 'Варианти',
    newVariant: 'Нов вариант',
    defaultName: 'Вариант {{n}}',
    emptyTitle: 'Все още няма варианти',
    emptyBody: 'Вариантът е една версия на плана ви, например „Наем“ или „Покупка“.',
    createFirst: 'Създаване на вариант',
    sharedItems: 'Споделени пера',
    assumptions: 'Допускания',
    addItem: 'Добавяне на перо',
    actions: 'Действия с варианта',
    edit: 'Редактиране на варианта',
    duplicateShared: 'Дублиране със споделени пера',
    duplicateCopy: 'Дублиране с копирани пера',
    copyName: '{{name}} (копие)',
    setBaseline: 'Задаване като базов',
    baseline: 'Базов',
    delete: 'Изтриване на варианта',
    deleted: 'Вариантът „{{name}}“ е изтрит',
    noItems: 'Все още няма пера',
    expand: 'Разгъване на {{name}}',
    collapse: 'Свиване на {{name}}',
    inCharts: 'Показване на {{name}} в графиките',
    showInCharts: 'Показване в графиките и сравненията',
    hideFromCharts: 'Скриване от графиките и сравненията',
  },
  groups: {
    income: 'Приходи',
    expense: 'Разходи',
    event: 'Еднократни събития',
    transfer: 'Спестявания и преводи',
    asset: 'Активи',
    liability: 'Задължения',
    rule: 'Правила',
  },
  cadence: {
    month: '/мес.',
    quarter: '/трим.',
    halfYear: '/6 мес.',
    year: '/год.',
    once: 'еднократно',
  },
  templates: {
    salary: 'Заплата',
    rentReceived: 'Получен наем',
    rentPaid: 'Наем',
    utilities: 'Сметки',
    livingCosts: 'Разходи за живот',
    subscription: 'Абонамент',
    bonus: 'Бонус',
    purchase: 'Покупка',
    inheritance: 'Наследство',
    savings: 'Спестовна сметка',
    investment: 'Инвестиции',
    property: 'Жилище',
    mortgage: 'Ипотека',
    consumerLoan: 'Потребителски кредит',
    incomeTax: 'Данък върху дохода',
    salaryTaxBg: 'Данъци върху заплата (България)',
    savingsRule: 'Правило за спестяване',
  },
  categories: {
    newIncome: 'Нова категория приходи',
    newExpense: 'Нова категория разходи',
    addIncome: 'Приход',
    addExpense: 'Разход',
    hint: 'Инфлацията по подразбиране важи за перата в категорията, които следват инфлацията.',
    kind: { income: 'Приход', expense: 'Разход' },
    name: 'Име на категорията',
    delete: 'Изтриване на категорията',
    inUse: 'Използва се от пера и не може да бъде изтрита',
    inflation: 'Инфлация за {{name}}',
    salary: 'Заплата',
    otherIncome: 'Други приходи',
    housing: 'Жилище',
    food: 'Храна',
    transport: 'Транспорт',
    utilities: 'Сметки',
    leisure: 'Свободно време',
    debt: 'Дългове',
    taxes: 'Данъци',
    otherExpense: 'Други',
  },
  itemActions: {
    copyTo: 'Копиране в',
    copiedInto: '„{{name}}“ е копирано в „{{variant}}“',
    menu: 'Действия с перото',
    duplicate: 'Дублиране',
    copyName: '{{name}} (копие)',
    shareWith: 'Използване и в',
    sharedIn_one: 'Използва се в {{count}} вариант',
    sharedIn_other: 'Използва се в {{count}} варианта',
    shared: 'Споделено',
    detach: 'Самостоятелно копие',
    removeFromVariant: 'Премахване от този вариант',
    delete: 'Изтриване',
    deleteEverywhere: 'Изтриване от всички варианти',
    deleted: '„{{name}}“ е изтрито',
    removed: '„{{name}}“ е премахнато от „{{variant}}“',
    sharedInto: 'Вече се използва и в „{{variant}}“',
    detached: '„{{name}}“ вече е самостоятелно в „{{variant}}“',
  },
  diagnostics: {
    formulaCycle: 'Това правило зависи от самото себе си и се пренебрегва.',
    formulaSourceMissing: 'Източник на това правило не е в този вариант и се брои за 0.',
    formulaNegativeClamped: 'Резултатът от правилото е отрицателен и е зададен на 0.',
    taxNegativeClamped:
      'Удържаният от началото на годината данък надвишава дължимия; възстановяване не се моделира, затова е зададен 0.',
    transferAccountMissing: 'Целевата сметка не е в този вариант, затова преводът се пренебрегва.',
    transferAccountClosed:
      'Някои преводи попадат в месеци, в които сметката не е открита, и се пропускат.',
    withdrawalCapped: 'Тегленията надвишават салдото и са ограничени до него.',
    itemStartsAfterHorizon: 'Започва след края на симулацията.',
    loanExtendsBeyondHorizon: 'Не е изплатен изцяло в рамките на симулацията.',
    paymentsBeyondHorizon:
      'Някои вноски са след края на симулацията; дотогава остатъкът се води като дължим.',
  },
  inspector: {
    impactNetWorth: 'Нетно състояние до {{year}}',
    impactMinCash: 'Най-ниска наличност',
    impactHint: 'Разлика със и без това перо в този вариант.',
    miniChartAria: '{{name}} във времето',
    label: 'Детайли',
    close: 'Затваряне на детайлите',
    usedIn: 'Използва се в',
    sections: {
      categories: 'Категории',
      insights: 'Във времето',
      amount: 'Сума',
      timing: 'Период',
      growth: 'Растеж',
      classification: 'Класификация',
      formula: 'Изчисление',
      tax: 'Данъчни правила',
      deductions: 'Удръжки',
      brackets: 'Скали',
      breakdown: 'Разбивка',
      account: 'Сметка',
      loan: 'Кредит',
      repayments: 'Предсрочни погасявания',
      fees: 'Такси',
      general: 'Общи',
      inflation: 'Инфлация',
      advanced: 'Разширени',
    },
  },
  fields: {
    inflation: 'Инфлация',
    inflationHint:
      'Замества инфлацията на категорията или общия ИПЦ. В периодите „По подразбиране“ важат те.',
    categoryDefault: 'По подразбиране за {{name}}',
    globalCpi: 'Общ ИПЦ',
    name: 'Име',
    direction: 'Посока',
    directionIn: 'Постъпление',
    directionOut: 'Плащане',
    amount: 'Сума',
    frequency: 'Честота',
    frequencies: {
      monthly: 'Месечно',
      quarterly: 'Тримесечно',
      semiannual: 'На 6 месеца',
      yearly: 'Годишно',
      once: 'Еднократно',
    },
    start: 'Начало',
    end: 'Край',
    noEnd: 'До края на симулацията',
    setEnd: 'Задаване на край',
    clearEnd: 'Премахване на края',
    growth: 'Растеж',
    growthKinds: {
      none: 'Остава същата',
      indexed: 'Следва инфлацията',
      rate: 'Собствен темп',
      steps: 'Стъпкови увеличения',
    },
    timing: 'Актуализиране',
    timings: { monthly: 'Всеки месец', annual: 'Веднъж годишно' },
    raise: 'Увеличение',
    everyMonths: 'На всеки (месеца)',
    basis: 'Сумата е в',
    basisToday: 'Днешни пари',
    basisAtStart: 'Пари към началото',
    category: 'Категория',
    noCategory: 'Без категория',
    account: 'Сметка за превод',
    noAccount: 'Няма — приход или разход',
    openingBalance: 'Начално салдо',
    opens: 'Открива се',
    fundedFromCash: 'Платено от наличността',
    fundedFromCashHint:
      'Покупката използва наличните пари (напр. жилище); иначе вече го притежавате.',
    instalments: 'Плащане на вноски',
    instalmentsHint:
      'Част от цената сега, останалото по-късно (напр. 20 % при договора, 80 % при завършване). Докато не е платено, остатъкът се дължи на продавача без лихва.',
    addInstalment: 'Добавяне на вноска',
    removeInstalment: 'Премахване на вноска {{n}}',
    instalmentMonth: 'Вноска {{n}}: месец',
    instalmentShare: 'Вноска {{n}}: дял от цената',
    instalmentRest: '{{share}}, остатъкът',
    liquidate: 'Продажба или теглене',
    liquidateAt: 'Изтегляне през',
    return: 'Годишна доходност',
    principal: 'Заета сума',
    termYears: 'Срок (години)',
    rate: 'Лихвен процент',
    amortization: 'Погасяване',
    annuity: 'Равни вноски',
    linear: 'Равни главници',
    disburse: 'Постъпва в наличността',
    disburseHint: 'Нов кредит, чиито пари постъпват в наличността. Изключете за съществуващ дълг.',
    upfrontFee: 'Еднократна такса',
    monthlyFee: 'Месечна такса',
    addRepayment: 'Добавяне на погасяване',
    removeRepayment: 'Премахване на погасяването',
    repaymentMonth: 'Месец',
    repaymentAmount: 'Сума',
    repaymentMode: 'Ефект',
    reduceTerm: 'По-кратък срок',
    reducePayment: 'По-ниска вноска',
    sources: 'Въз основа на',
    addSource: 'Добавяне на източник',
    removeSource: 'Премахване на източника',
    add: 'Прибавяне',
    subtract: 'Изваждане',
    totalIncome: 'Общо приходи',
    totalExpenses: 'Общо разходи',
    accountReturn: 'Доходност от {{name}}',
    ruleKind: 'Вид правило',
    ruleKinds: { percentOf: 'Процент', tax: 'Данък' },
    percent: 'Процент',
    floor: 'Минимум на месец',
    cap: 'Максимум на месец',
    noLimit: 'Няма',
    scheduled: 'Променя се във времето — редакторът на графици идва в следваща версия.',
    invalidNumber: 'Въведете число',
    outOfRange: 'Трябва да е между {{min}} и {{max}}',
    required: 'Задължително',
    month: 'Месец',
    year: 'Година',
  },
  tax: {
    preset: 'Правила',
    custom: 'Собствени',
    presets: {
      'bg-employment-2026': 'България — трудово правоотношение, 2026',
      'bg-interest-2026': 'България — облагаеми лихви, 2026',
    },
    presetHint: 'Данните са проверени към {{date}}; изборът на правила заменя стойностите по-долу.',
    customHint: 'Собствени правила. Изберете готови, за да започнете от официалните данни.',
    sources: 'Официални източници',
    contributions: 'Осигурителни вноски',
    newDeduction: 'Удръжка {{n}}',
    addDeduction: 'Добавяне на удръжка',
    removeDeduction: 'Премахване на {{name}}',
    noDeductions: 'Нищо не се приспада преди данъка.',
    deductionName: 'Име',
    deductionRate: 'Ставка',
    deductionFloor: 'Минимална основа',
    deductionCap: 'Максимална основа',
    perMonth: 'Месечно, преди ставката',
    emit: 'Също се плаща',
    emitHint: 'Вноските също се плащат, затова намаляват паричните средства.',
    bracketsHint: 'Годишен облагаем доход. Една скала означава плосък данък.',
    bracketFrom: 'От, годишно',
    bracketRate: 'Ставка',
    bracketN: 'Скала {{n}}',
    addBracket: 'Добавяне на скала',
    removeBracket: 'Премахване на скала {{n}}',
    allowance: 'Необлагаем размер, годишно',
    credits: 'Данъчни облекчения, годишно',
    settlement: 'Удържане',
    settlements: { monthly: 'Всеки месец отделно', annualCumulative: 'От началото на годината' },
    settlementHint: '„От началото на годината“ е подходящо за прогресивни скали и нередовен доход.',
    indexThresholds: 'Прагове',
    indexings: { none: 'Фиксирани', cpi: 'Следват инфлацията' },
    breakdownMonth: 'Месец',
    gross: 'Облаган доход',
    taxable: 'Облагаема основа',
    incomeTax: 'Данък върху дохода',
    net: 'Нето',
    effectiveRate: 'Ефективна ставка',
    breakdownEmpty: 'През този месец нищо не се облага.',
    notDeducted: 'само намалява облагаемата основа',
  },
  mc: {
    running: 'Монте Карло {{percent}}',
    runningLabel: 'Напредък на Монте Карло',
    cancel: 'Отказ от симулацията Монте Карло',
    done: '{{paths}} пътя · {{seconds}} с',
    doneHint:
      'Случайни пътища за инфлацията и доходността, изтеглени от началното число на проекта: едно и също число дава един и същ резултат.',
    error: 'Монте Карло не успя',
    median: 'Медиана',
    bandsCaption: 'Медиана с диапазони P10–P90 и P5–P95',
    averagePath: 'Среден път',
    inner: 'P10–P90',
    outer: 'P5–P95',
    range: '{{from}} – {{to}}',
    averagePathNote:
      'Този изглед показва средния път (всяка ставка е на средната си стойност). Диапазонът от Монте Карло е в „Обзор“ и „Риск“.',
    pending: 'Симулиране на пътищата…',
    noUncertainty:
      'Засега нищо в плана не е несигурно, затова всички пътища са еднакви. Задайте инфлацията или доходност като „Несигурна“, за да видите диапазон.',
    openAssumptions: 'Отваряне на допусканията',
  },
  risk: {
    enableTitle: 'Вижте диапазона от резултати',
    enableBody:
      'Монте Карло симулира хиляди случайни пътища за инфлацията и доходността. Включете го, за да видите диапазони, вероятности и цели.',
    enable: 'Стартиране на Монте Карло',
    metric: 'Показател',
    fanTitle: '{{metric}}: диапазон от резултати',
    fanAria: '{{metric}} за {{name}}: медиана с диапазони P10–P90 и P5–P95',
    pCashNegative: 'Вероятност за отрицателна наличност',
    pCashNegativeAria: 'Дял на пътищата с отрицателна наличност по месеци за всеки вариант',
    inMonth: 'През месеца',
    ever: 'В някой момент досега',
    histogramTitle: 'Разпределение на нетното състояние',
    histogramAt: 'края на {{date}}',
    histogramAria: 'Нетно състояние по {{paths}} пътя в края на {{date}} за {{name}}',
    histogramHint: 'Посочете графиката на диапазона, за да изберете година.',
    bin: 'от {{from}} до {{to}}: {{share}} от пътищата',
    goals: 'Цели',
    goalsEmpty: 'Все още няма цели. Добавете цел, за да видите колко е вероятна.',
    addGoal: 'Добавяне на цел',
    goalLine: '{{metric}} поне {{target}} до {{date}}',
    goalLineReal: '{{metric}} поне {{target}} (в днешни пари) до {{date}}',
    goalChanceAria: '{{name}}: вероятност {{chance}}',
  },
  keyDates: {
    title: 'Ключови дати',
    description:
      'Назовете датите, от които зависи планът, и обвържете други дати с тях: преместването на ключова дата премества всичко, обвързано с нея.',
    name: 'Име на ключовата дата',
    newName: 'Ключова дата {{n}}',
    month: 'Месец',
    add: 'Добавяне на ключова дата',
    remove: 'Премахване на {{name}}',
    untied: 'Още нищо не е обвързано с нея',
    tied_one: '{{count}} дата е обвързана с нея',
    tied_other: '{{count}} дати са обвързани с нея',
    keyDate: 'Ключова дата',
    offset: 'Месеци след ключовата дата',
    monthsShort: 'мес.',
    tie: 'Обвързване на „{{field}}“ с ключова дата',
    untie: 'Отвързване на „{{field}}“ от ключовата дата',
    afterHorizon: 'След края на симулацията, затова свързаните с нея дати остават извън нея.',
  },
  goals: {
    title: 'Цели',
    description: 'Цели, чиято вероятност се отчита при симулациите Монте Карло.',
    name: 'Име на целта',
    newName: 'Цел {{n}}',
    metric: 'Показател',
    metrics: { netWorth: 'Нетно състояние', cash: 'Наличност' },
    target: 'Поне',
    by: 'До',
    real: 'В днешни пари',
    add: 'Добавяне на цел',
    remove: 'Премахване на {{name}}',
    metOnAverage: 'Постигната при средния път',
    notMetOnAverage: 'Непостигната при средния път',
  },
  rate: {
    describeCpi: 'ИПЦ {{offset}}',
    describeDistribution: '≈{{mean}} ±{{vol}}',
    kind: 'Вид ставка',
    kinds: {
      inherit: 'По подразбиране',
      fixed: 'Фиксирана',
      cpiRelative: 'Инфлация +',
      distribution: 'Несигурна',
    },
    rate: 'Ставка',
    offset: 'Над инфлацията',
    mean: 'Средно',
    volatility: 'Волатилност',
    volatilityHint: 'Използва се при Монте Карло; детерминистичният режим използва средното.',
  },
  assumptions: {
    title: 'Допускания',
    description: 'Общи настройки за всички варианти.',
    start: 'Начален месец',
    horizon: 'Хоризонт (години)',
    startingCash: 'Начална наличност',
    currency: 'Валута',
    cpi: 'Инфлация (ИПЦ)',
    overdraft: 'Лихва по овърдрафт',
    overdraftHint: 'Начислява се при отрицателна наличност. Оставете 0 за без лихва.',
    seed: 'Начално число за случайност',
    mcPaths: 'Пътища за Монте Карло',
  },
  scenario: {
    title: 'Вариант',
    name: 'Име',
    color: 'Цвят',
    colorN: 'Цвят {{n}}',
    baseline: 'Базов вариант',
    baselineHint: 'Другите варианти се сравняват с базовия.',
    items_one: '{{count}} перо',
    items_other: '{{count}} пера',
  },
  charts: {
    viewAsTable: 'Показване като таблица',
    viewAsChart: 'Показване като графика',
    year: 'Година',
    partialYear: '{{year}} (непълна година)',
    item: 'Перо',
    total: 'Общо',
    share: 'Дял',
    other: 'Други',
    uncategorized: 'Без категория',
    income: 'Приходи',
    expenses: 'Разходи',
    incomeVsExpenses: 'Приходи и разходи',
    incomeVsExpensesAria: 'Годишни приходи и разходи за {{name}}',
    cashFlowTitle: 'Паричен поток по категории',
    cashFlowAria:
      'Годишни приходи (над нулата) и разходи (под нулата) по категории за {{name}}, с нетния паричен поток',
    netFlow: 'Нетен паричен поток',
    savingsRate: 'Норма на спестяване',
    savingsRateAria: 'Дял от доходите, който остава след разходите, по години, за {{name}}',
    topItems: 'Най-големи пера',
    balanceTitle: 'Активи и задължения',
    balanceAria:
      'Наличност, активи (над нулата) и кредити (под нулата) във времето за {{name}}, с нетното състояние',
    allocation: 'Разпределение',
    allocationAria: 'Дял на притежанията през {{date}}',
    debtTitle: 'Дълг по кредити',
    debtAria: 'Остатък по кредитите във времето за {{name}}',
    interestVsPrincipal: 'Лихва и главница',
    interestVsPrincipalAria: 'Годишно платени лихви и главници за {{name}}',
    interest: 'Лихва',
    principal: 'Главница',
    noDebt: '{{name}} няма кредити.',
    range: 'Период',
    rangeStart: 'Начало на периода',
    rangeEnd: 'Край на периода',
    resetRange: 'Всички години',
    events: {
      loanPaidOff: '{{name}} е изплатен',
      firstNegativeCash: 'Наличността става отрицателна',
      assetLiquidated: '{{name}} е изтеглено',
    },
    netWorth: 'Нетно състояние',
    cash: 'Наличност',
    owedFor: '{{name}}: дължимо на продавача',
    cashAria: 'Парична наличност във времето; месеците с отрицателна наличност са оцветени',
    legend: 'Варианти',
    viewSoon: 'Този изглед идва в следваща версия.',
    noData: 'Добавете вариант с няколко пера, за да видите графиките.',
    inMoney: 'в пари от {{year}}',
    negative: 'Отрицателна наличност',
  },
  schedule: {
    varyOverTime: 'Промяна във времето',
    title: '{{label}} във времето',
    description: 'Всеки период важи от началния си месец до началото на следващия.',
    summary_one: '{{count}} период: {{from}}',
    summary_other: '{{count}} периода: {{from}} → {{to}}',
    from: 'От',
    value: 'Стойност',
    actions: 'Действия',
    period: 'Период {{n}}',
    fromStart: 'Начало ({{date}})',
    split: 'Разделяне на периода',
    remove: 'Премахване на периода',
    add: 'Добавяне на период',
    preview: 'Преглед',
    previewAria: '{{title}}: преглед',
    useSingle: 'Една стойност',
    confirmSingle: 'Да се запази само стойността на първия период?',
    save: 'Запазване',
    errors: {
      duplicate: 'Два периода започват в един и същ месец',
      outOfRange: 'Започва след края на симулацията',
    },
  },
  compare: {
    baseline: 'Базов вариант',
    emptyTitle: 'Сравняване на варианти',
    emptyBody:
      'За сравнение са нужни базов вариант и поне още един вариант, показан в графиките. Дублирайте вариант, променете нещо и вижте разликата.',
    duplicate: 'Дублиране на {{name}} за сравнение',
    storyTitle: 'Обобщение',
    ahead: '{{name}} завършва с {{amount}} пред {{baseline}} през {{year}}',
    behind: '{{name}} завършва с {{amount}} след {{baseline}} през {{year}}',
    level: '{{name}} завършва наравно с {{baseline}} през {{year}}',
    overtakes: 'Излиза напред окончателно през {{date}}',
    fallsBehind: 'Изостава окончателно през {{date}}',
    throughoutAhead: 'Води през цялото време.',
    throughoutBehind: 'Изостава през цялото време.',
    mcBeats:
      'Води в {{chance}} от пътищата през {{year}}; медианна разлика {{median}} (P10–P90: от {{from}} до {{to}}).',
    overlayTitle: '{{metric}} по варианти',
    diffTitle: 'Разлика спрямо {{baseline}}',
    diffCaption: 'Нетно състояние минус това на базовия вариант; над нулата означава преднина',
    diffAria: 'Нетно състояние на всеки вариант минус това на {{baseline}} във времето',
    pBeatsTitle: 'Вероятност да изпревари {{baseline}}',
    pBeatsAria:
      'Дял на пътищата, при които нетното състояние на всеки вариант надвишава това на {{baseline}}, по месеци',
    movesAhead: '{{name}} излиза напред',
    fallsBehindEvent: '{{name}} изостава',
    metricsTitle: 'Основни показатели',
    figure: 'Показател',
    vsBaseline: '{{delta}} спрямо базовия',
    rows: {
      netWorthEnd: 'Нетно състояние, {{year}}',
      lowestCash: 'Най-ниска наличност',
      debtFree: 'Без дългове от',
      interest: 'Платени лихви',
      income: 'Общо приходи',
      expenses: 'Общо разходи',
      medianNetWorth: 'Медианно нетно състояние, {{year}}',
      pCashNegative: 'Риск от отрицателна наличност',
      pBeats: 'Вероятност да изпревари базовия',
    },
  },
  kpi: {
    medianNetWorthEnd: 'Медианно нетно състояние, {{year}}',
    p10p90: 'P10–P90: {{from}} – {{to}}',
    pCashNegative: 'Риск от отрицателна наличност',
    pCashNegativeDetail: 'в някой момент, от {{paths}} пътя',
    label: 'Ключови стойности за {{name}}',
    netWorthEnd: 'Нетно състояние, {{year}}',
    vsBaseline: '{{delta}} спрямо {{name}}',
    lowestCash: 'Най-ниска наличност',
    inMonth: 'през {{date}}',
    debtFree: 'Без дългове',
    noDebt: 'Няма дългове',
    notWithinHorizon: 'Не в рамките на хоризонта',
    interestPaid: 'Платени лихви',
    nominal: 'Номинално, за целия хоризонт',
  },
  returns: {
    analyse: 'Анализ на възвръщаемостта',
    open: 'Към възвръщаемостта',
    section: 'Възвръщаемост',
    sectionHint:
      'IRR, печалба и коефициент на тази инвестиция, все едно е продадена в който и да е месец.',
    investment: 'Инвестиция',
    investmentHint: 'Включва се в изгледа „Възвръщаемост“.',
    noInvestment: 'Няма',
    settings: 'Настройки на инвестицията',
    name: 'Име на инвестицията',
    scope: 'Какво включва',
    scopes: { variant: 'Целия вариант', items: 'Избрани пера' },
    scopeHints: {
      variant:
        'Броят се всички пера във варианта. Изберете това, когато проектът съдържа само инвестицията: парите за нея излизат от наличността, а приходите се връщат там.',
      items: 'Броят се само перата, включени по-долу (за {{name}}).',
    },
    itemsLabel: 'Пера в тази инвестиция',
    add: 'Нова инвестиция',
    newName: 'Инвестиция {{n}}',
    remove: 'Премахване на анализа',
    kpiLabel: 'Възвръщаемост на {{name}}',
    irr: 'IRR',
    irrHint:
      'Годишната доходност на собствените ви пари, като се отчита времето: парите, вложени по-рано, тежат повече.',
    profit: 'Печалба',
    profitHint:
      'Всичко получено плюс това, което би донесла продажба след погасяване на кредитите, минус всичко вложено.',
    multiple: 'Коефициент',
    multipleHint:
      'Получените пари, заедно с продажбата, на всяка вложена единица. ×2 означава, че парите ви са се удвоили.',
    multipleDetail: 'получени ÷ вложени пари',
    peak: 'Най-много вложени пари',
    peakHint: 'Най-голямата сума собствени пари, която инвестицията е изисквала досега.',
    peakDetail: 'собствени пари, в най-тежкия момент',
    ifSoldIn: 'при продажба през {{date}}, без разходите по продажбата',
    afterSale: 'продадено до {{date}}',
    profitTitle: 'Печалба при продажба',
    irrTitle: 'IRR при продажба',
    moneyTitle: 'Вашите пари',
    cumulative: 'Получени − вложени',
    equity: 'Собствен капитал (активи − кредити)',
    profitAria: 'Печалба при продажба в края на периода: {{values}}',
    irrAria: 'IRR при продажба в края на периода: {{values}}',
    moneyAria:
      'Вашите пари в {{name}} в края на периода: получени минус вложени {{cumulative}}, собствен капитал {{equity}}',
  },
  explain: {
    title: 'Какво се случи през {{date}}',
    description: 'Всички парични движения в „{{name}}“ през този месец.',
    opening: 'Наличност в началото на месеца',
    closing: 'Наличност в края на месеца',
    noMovements: 'Няма парични движения през този месец.',
    goToItem: 'Отваряне на {{name}}',
    previous: 'Предишен месец',
    next: 'Следващ месец',
    nominalNote: 'Номинални суми',
    assets: 'Активи',
    liabilities: 'Задължения',
    netWorth: 'Нетно състояние',
  },
  ledger: {
    title: 'Регистър — {{name}}',
    nominalHint: 'Номинални суми. Потоците са сумирани за периода; салдата са към края му.',
    exportCsv: 'Експорт в CSV',
    expandYear: 'Показване на месеците от {{year}} г.',
    collapseYear: 'Скриване на месеците от {{year}} г.',
    overdraft: 'Лихва по овърдрафт',
    netCashFlow: 'Нетен паричен поток',
    cashEnd: 'Наличност',
    assetsEnd: 'Активи',
    liabilitiesEnd: 'Задължения',
    netWorthEnd: 'Нетно състояние',
    partialNote: '* Непълна година (симулацията започва или завършва в нея).',
  },
  visibility: {
    mute: 'Изключване',
    unmute: 'Включване',
    solo: 'Само това',
    unsolo: 'Всички пера',
    muted: 'Изключено',
    soloed: 'Само това',
    whatIf: 'Активен сценарий „ами ако“',
    whatIfHint:
      'Изключените пера и режимът „само това“ не влизат в симулацията. Щракнете за нулиране.',
    reset: 'Нулиране',
  },
  shared: {
    title: 'Споделени пера',
    description: 'Пера, използвани в повече от един вариант. Промяната им важи навсякъде.',
    emptyTitle: 'Няма споделени пера',
    emptyBody:
      'Използвайте „Използване и в“ или ги плъзнете върху друг вариант, за да ги споделите.',
  },
  status: {
    saved: 'Запазено',
    unsaved: 'Незапазени промени',
    saving: 'Запазване…',
    error: 'Не е запазено',
    errorHint:
      'Запазването в този браузър не успя. Промените се пазят, докато този раздел е отворен.',
    calculated: '{{ms}} мс',
    calculatedHint: 'Време за симулация',
    calculating: 'Изчисляване…',
    simError: 'Симулацията не успя',
    local: 'Съхранява се в този браузър',
  },
  history: {
    undo: 'Отмяна',
    redo: 'Повторение',
  },
  account: {
    signIn: 'Вход',
    signInToSync: 'Вход за синхронизиране',
    signInHint: 'Пазете проектите си в профила и ги отваряйте от всяко устройство.',
    menu: 'Профил',
    signedInAs: 'Влезли сте като {{email}}',
    signOut: 'Изход',
  },
  signIn: {
    title: 'Вход в Kasora',
    body: 'Проектите ви се синхронизират между устройствата. Без профил всичко остава в този браузър.',
    google: 'Продължаване с Google',
    github: 'Продължаване с GitHub',
    email: 'Имейл',
    sendLink: 'Изпратете ми връзка за вход',
    sending: 'Изпращане…',
    or: 'или',
    checkTitle: 'Проверете имейла си',
    checkBody:
      'Изпратихме връзка за вход на {{email}}. Тя работи веднъж, 15 минути, в този браузър.',
    useDifferent: 'Друг имейл',
    unavailable: 'Тук няма профили. Проектите ви остават в този браузър.',
    back: 'Към проектите',
    verifying: 'Влизане…',
    errors: {
      rate_limited: 'Твърде много имейли за вход. Изчакайте няколко минути и опитайте отново.',
      bad_request: 'Въведете валиден имейл адрес.',
      link_invalid: 'Тази връзка за вход е използвана или е невалидна. Поискайте нова.',
      link_expired: 'Тази връзка за вход е изтекла. Поискайте нова.',
      link_other_browser:
        'Отворете връзката в браузъра, от който я поискахте, или поискайте нова оттук.',
      oauth_failed: 'Входът чрез тази услуга не успя. Опитайте отново.',
      email_unverified: 'Този профил няма потвърден имейл адрес.',
      provider_unavailable: 'Този начин за вход не е наличен.',
      email_unavailable: 'Входът с имейл в момента не е наличен.',
      generic: 'Нещо се обърка. Опитайте отново.',
    },
  },
  sync: {
    synced: 'Синхронизирано',
    syncedHint: 'Запазено в профила ви',
    pending: 'Запазване…',
    syncing: 'Синхронизиране…',
    offline: 'Офлайн',
    offlineHint: 'Промените са запазени в този браузър и ще се синхронизират, когато сте онлайн.',
    conflict: 'Конфликт',
    localOnly: 'Само в този браузър',
    localOnlyHint:
      'Не е в профила ви. Импортирайте го от страницата с проекти, за да се синхронизира.',
    conflictTitle: 'Променено на друго устройство',
    conflictBody:
      'Проектът „{{name}}“ беше запазен на друго устройство {{time}}, след като започнахте да редактирате тук. Коя версия да запазим?',
    keepMine: 'Моята',
    keepMineHint: 'Другата версия се заменя с тази',
    takeTheirs: 'Другата версия',
    takeTheirsHint: 'Промените оттук се отхвърлят',
    keepBoth: 'И двете',
    keepBothHint: 'Използва се другата версия, а вашата се запазва като копие',
    conflictCopy: '{{name}} (моето копие)',
    importTitle: 'Да добавим ли тези проекти в профила ви?',
    importBody:
      'Те са само в този браузър. Импортирайте ги, за да ги отваряте и от другите си устройства.',
    import_one: 'Импортиране на {{count}} проект',
    import_other: 'Импортиране на {{count}} проекта',
    notNow: 'Не сега',
  },
  share: {
    button: 'Сподели',
    hint: 'Споделете връзка за преглед на проекта',
    palette: 'Споделяне…',
    title: 'Споделяне на „{{name}}“',
    description:
      'Всеки с връзката може да разгледа проекта (вариантите, перата и графиките) без профил, но не може да го променя.',
    loading: 'Проверка за връзка…',
    none: 'Проектът все още няма връзка.',
    create: 'Създай връзка',
    link: 'Връзка за споделяне',
    copy: 'Копирай',
    copied: 'Връзката е копирана',
    copyFailed: 'Връзката не можа да бъде копирана. Маркирайте я и я копирайте сами.',
    language: 'Език',
    languageViewer: 'Езикът на зрителя',
    includeView: 'Отвори в текущия изглед',
    includeViewHint: 'Връзката отваря този изглед със същия вариант, перо и времеви диапазон.',
    latestNote:
      'Връзката винаги показва последната запазена версия. Името на проекта се вижда в прегледите на връзки.',
    reset: 'Нова връзка',
    stop: 'Спри споделянето',
    confirmReset: 'Текущата връзка ще спре да работи.',
    confirmStop: 'Връзката ще спре да работи. По-късно можете да споделите отново с нова връзка.',
    signInFirst: 'Влезте, за да споделяте: връзката показва копието, запазено в профила ви.',
    localOnly: 'Проектът е само в този браузър. Добавете го в профила си, за да го споделите.',
    errors: {
      offline: 'Няма връзка с интернет. Опитайте отново, когато се свържете.',
      notUploaded: 'Проектът все още се запазва в профила ви. Опитайте отново след малко.',
      failed: 'Нещо се обърка. Опитайте отново.',
    },
  },
  viewer: {
    viewOnly: 'Само за преглед',
    viewOnlyHint:
      'Споделен проект: можете да го разглеждате, но не и да го променяте тук. Запазете копие, за да го редактирате.',
    saveCopy: 'Запази копие',
    saveCopyHint: 'Копира проекта във вашите проекти, където можете да го редактирате',
    copied: 'Копие на „{{name}}“ е запазено във вашите проекти',
    documentTitle: '{{name}} · Kasora',
    loading: 'Отваряне на споделения проект…',
    reload: 'Презареди',
    openKasora: 'Отвори Kasora',
    problems: {
      notFound: {
        title: 'Тази връзка вече не работи',
        body: 'Собственикът може да е спрял споделянето, да е създал нова връзка или да е изтрил проекта.',
      },
      outdated: {
        title: 'Kasora е обновена',
        body: 'Проектът е запазен с по-нова версия на Kasora. Презаредете страницата, за да го отворите.',
      },
      error: {
        title: 'Проектът не може да бъде отворен',
        body: 'Проверете интернет връзката си и опитайте отново.',
      },
    },
  },
  compact: {
    readOnly:
      'Само за преглед на малки екрани. Отворете Kasora на по-голям екран, за да редактирате.',
  },
  palette: {
    title: 'Команди',
    placeholder: 'Въведете команда или търсене…',
    empty: 'Няма съвпадащи команди.',
    groups: {
      actions: 'Действия',
      goTo: 'Отиване до',
      view: 'Изглед',
      preferences: 'Настройки',
      help: 'Помощ',
    },
    newItem: 'Ново перо: {{name}}',
    goToView: 'Към {{name}}',
    variant: 'Вариант: {{name}}',
    item: '{{name}} · {{variant}}',
    showReal: 'Реални стойности (в днешни пари)',
    showNominal: 'Номинални стойности',
    runMonteCarlo: 'Стартиране на Монте Карло',
    deterministic: 'Обратно към детерминистичен режим',
    theme: 'Тема: {{name}}',
    language: 'Език: {{name}}',
    tour: 'Кратко запознаване',
    projects: 'Към проектите',
  },
  shortcuts: {
    title: 'Клавишни комбинации',
    then: 'после',
    or: 'или',
    groups: { general: 'Общи', navigation: 'Навигация', editing: 'Редактиране', view: 'Изглед' },
    items: {
      palette: 'Команди',
      help: 'Клавишни комбинации',
      moveDown: 'Следващо перо',
      moveUp: 'Предишно перо',
      open: 'Отваряне',
      views: 'Изгледи',
      toggleVariants: 'Панел с варианти',
      toggleInspector: 'Панел с детайли',
      assumptions: 'Допускания',
      createItem: 'Ново перо',
      newVariant: 'Нов вариант',
      editValue: 'Редактиране на основната стойност',
      duplicate: 'Дублиране',
      delete: 'Изтриване (с отмяна)',
      mute: 'Изключване',
      solo: 'Само това',
      undo: 'Отмяна',
      redo: 'Повторение',
      realNominal: 'Реални / номинални',
      monteCarlo: 'Монте Карло вкл. / изкл.',
      exportReport: 'Експорт на отчет в PDF',
    },
  },
  tour: {
    label: 'Запознаване, стъпка {{step}} от {{total}}',
    tree: {
      title: 'Вашите варианти',
      body: 'Всеки вариант е версия на плана ви. Перата му — приходи, разходи, сметки, кредити и правила — са изброени тук. Натиснете C, за да добавите ново.',
    },
    charts: {
      title: 'Графиките',
      body: 'Всяка промяна се преизчислява веднага. Сменяйте изгледите с 1–7 и щракнете върху месец, за да видите какво се е случило в него.',
    },
    palette: {
      title: 'Всичко от клавиатурата',
      body: 'Натиснете ⌘K (Ctrl+K) за команди и ? за всички клавишни комбинации.',
    },
    next: 'Напред',
    done: 'Готово',
    skip: 'Пропускане',
  },
  home: {
    title: 'Проекти',
    newProject: 'Нов проект',
    emptyTitle: 'Планирайте финансите си',
    emptyBody: 'Започнете от шаблон или празен план. Всичко остава в този браузър.',
    edited: 'Редактиран {{time}}',
    variants_one: '{{count}} вариант',
    variants_other: '{{count}} варианта',
    open: 'Отваряне',
    actions: 'Действия с проекта',
    delete: 'Изтриване на проекта',
    deleteTitle: 'Изтриване на „{{name}}“?',
    deleteBody: 'Проектът ще бъде премахнат от този браузър. Това не може да бъде отменено.',
    sampleName: '{{name}} (пример)',
    deleteBodySynced:
      'Проектът отива в кошчето на профила ви, откъдето може да бъде възстановен в рамките на 30 дни.',
    localOnly: 'Само в този браузър',
    addToAccount: 'Добавяне в профила',
    trash: 'Кошче',
    trashHint: 'Изтритите проекти могат да бъдат възстановени в рамките на 30 дни.',
    trashEmpty: 'Кошчето е празно.',
    restore: 'Възстановяване',
    deleted: 'Изтрит {{time}}',
    exportJson: 'Експорт като JSON',
    importJson: 'Импорт на JSON',
    importFailed: 'Този файл не е проект на Kasora.',
    imported: 'Импортиран „{{name}}“',
  },
  createProject: {
    title: 'Нов проект',
    description: 'Изберете отправна точка. Можете да промените всичко по-късно.',
    name: 'Име на проекта',
    defaultName: 'Моят план',
    create: 'Създаване на проект',
    templates: {
      blank: { title: 'Празен', body: 'Един празен вариант и стандартни допускания.' },
      rentVsBuy: {
        title: 'Наем или покупка',
        body: 'Два варианта със същия доход: живот под наем или покупка на жилище с ипотека.',
      },
      retireEarly: {
        title: 'Пенсиониране на 55',
        body: 'Спирате работа на 55 или на 60 и живеете от инвестициите си до държавната пенсия.',
      },
      buyToLet: {
        title: 'Жилище под наем, купено на зелено',
        body: 'Апартамент на зелено с ипотека, отдаван под наем и продаден след 10 години, срещу депозит. С анализ на възвръщаемостта.',
      },
      earlyPayoff: {
        title: 'Предсрочно изплащане на ипотека',
        body: 'Една и съща ипотека, изплащана по план или с годишни вноски.',
      },
    },
    items: {
      investing: 'Инвестиране',
      homeUpkeep: 'Поддръжка на жилището',
      purchaseCosts: 'Разходи по покупката',
      pension: 'Държавна пенсия',
      drawdown: 'Издръжка от инвестициите',
      furnishing: 'Довършване и обзавеждане',
      runningCosts: 'Разходи и незаетост',
      sellingCosts: 'Разходи по продажбата',
      deposit: 'Депозит',
      flat: 'Жилище под наем',
    },
    keyDates: {
      completion: 'Завършване на сградата',
      sale: 'Продажба',
    },
    variants: {
      plan: 'План',
      rent: 'Наем',
      buy: 'Покупка',
      onSchedule: 'По план',
      lumpSums: 'Годишни вноски',
      at55: 'Пенсиониране на 55',
      at60: 'Пенсиониране на 60',
      offPlan: 'Покупка на зелено',
      deposit: 'Пари в депозит',
    },
  },
  errors: {
    projectNotFound: 'Проектът не беше намерен.',
    projectInvalid: 'Проектът не може да бъде отворен.',
    futureVersion: 'Проектът е запазен с по-нова версия на Kasora.',
    chartFailed: 'Тази графика не можа да бъде начертана.',
    viewFailed: 'Този изглед не можа да бъде показан.',
    appFailed: 'Нещо се обърка',
    appFailedBody: 'Работата ви е запазена в този браузър. Презареждането обикновено помага.',
    tryAgain: 'Опитайте отново',
    reload: 'Презареждане',
    commandRejected: 'Тази промяна не е валидна.',
    commandRejectedFor: 'Тази промяна не е валидна: тя би нарушила „{{name}}“.',
  },
  settings: {
    title: 'Настройки',
    theme: 'Тема',
    themeDark: 'Тъмна',
    themeLight: 'Светла',
    themeSystem: 'Системна',
    language: 'Език',
    languageEn: 'English',
    languageBg: 'Български',
  },
  panels: {
    toggleVariants: 'Показване/скриване на вариантите',
    toggleInspector: 'Показване/скриване на детайлите',
    resize: 'Преоразмеряване на панела',
  },
  report: {
    dialog: {
      button: 'Отчет',
      hint: 'Експорт на отчет за плана в PDF',
      palette: 'Експорт на отчет в PDF',
      title: 'Експорт на отчет в PDF',
      description: 'Отчет за този план, създаден на това устройство.',
      variants: 'Варианти',
      noVariants: 'Изберете поне един вариант.',
      sections: 'Раздели',
      sectionNames: {
        summary: 'Обобщение',
        assumptions: 'Допускания',
        variants: 'Раздел за всеки вариант',
        comparison: 'Сравнение',
        risk: 'Диапазон на резултатите (Монте Карло)',
        appendix: 'Приложение: по години, методика и източници',
      },
      comparisonNeedsTwo: 'Нужни са поне два варианта.',
      riskNeedsMc: 'Нужна е симулация по Монте Карло.',
      runMc: 'Пусни Монте Карло',
      riskRunning: 'Монте Карло се изпълнява…',
      values: 'Стойности',
      language: 'Език',
      pageSize: 'Размер на страницата',
      letter: 'Letter (САЩ)',
      overrides:
        'Изключените или изолираните пера не влизат в отчета, както и в графиките. Върнете ги, за да отчетете запазения план.',
      export: 'Експорт в PDF',
      progress: 'Напредък на отчета',
      preparing: 'Отчетът се подготвя…',
      rendering: 'Страниците се подреждат… {{percent}}',
      cancelExport: 'Спри',
      failed: 'Отчетът не можа да бъде създаден. Опитайте отново.',
      saved: 'Запазено: {{name}}',
    },
    subject: 'Финансов план за {{names}}',
    contents: 'Съдържание',
    continued: '(продължение)',
    pageOf: 'Страница {page} от {pages}',
    realMoney: 'Сумите са в пари от {{year}} (коригирани с инфлацията)',
    nominalMoney: 'Сумите са номинални (такива, каквито ще бъдат в съответната година)',
    nominalCaption: 'Номинални суми',
    realCaption: 'В пари от {{year}}',
    years_one: '{{years}} година',
    years_other: '{{years}} години',
    none: 'Няма',
    fromDate: '{{value}} от {{date}}',
    period: '{{from}} – {{to}}',
    periodOpen: 'От {{from}}',
    inflation: 'Инфлация',
    steps_one: '{{raise}} всеки месец',
    steps_other: '{{raise}} на всеки {{count}} месеца',
    loanRate_one: '{{rate}} за {{years}} година',
    loanRate_other: '{{rate}} за {{years}} години',
    cover: {
      eyebrow: 'Отчет за финансов план',
      subtitle: '{{from}} – {{to}} · {{years}}',
      variants: 'Варианти',
      values: 'Стойности',
      simulation: 'Симулация',
      monteCarlo: 'Монте Карло, {{paths}} пътя',
      date: 'Изготвен',
      note: 'Създаден с Kasora изцяло във вашия браузър: за изготвянето му не са изпратени никакви данни от плана. Числата са прогнози въз основа на допусканията в плана, а не предсказания или финансов съвет.',
    },
    summary: {
      title: 'Обобщение',
      // Not ending on a date: Bulgarian years end in „г.“, which would double the full stop.
      intro_one: 'Отчетът обхваща {{names}} от {{from}} до {{to}}, за период от {{period}}.',
      intro_other:
        'Отчетът сравнява {{count}} варианта ({{names}}) от {{from}} до {{to}}, за период от {{period}}.',
      mc: 'Диапазоните са изчислени от {{paths}} пътя по метода Монте Карло.',
      verdict: 'Как се сравняват вариантите',
    },
    goals: {
      goal: 'Цел',
      target: 'Условие',
      met: 'Постигната',
      notMet: 'Непостигната',
      mcCaption: 'Вероятност за постигане на всяка цел, от {{paths}} пътя',
      averageCaption: 'При средния път, с всеки процент на средната му стойност',
    },
    assumptions: {
      intro: 'Всички варианти използват тези настройки.',
      setting: 'Настройка',
      value: 'Стойност',
      keyDates: 'Ключови дати',
      horizon: 'Хоризонт',
      values: 'Стойности',
      simulation: 'Симулация',
      monteCarlo:
        'Монте Карло: {{paths}} случайни пътя за инфлацията и доходността, начално число {{seed}}. Едно и също начално число винаги дава един и същ резултат.',
    },
    variant: {
      title: 'Вариант: {{name}}',
      items: 'Пера',
      type: 'Вид',
      amount: 'Сума',
      when: 'Период',
      rate: 'Ръст или ставка',
    },
    comparison: {
      title: 'Сравнение',
      intro:
        'Всеки вариант се сравнява с базовия – {{baseline}}. Всички варианти използват едни и същи допускания, а при Монте Карло – едни и същи случайни пътища, затова разликите идват от решенията във всеки вариант, а не от случайността.',
    },
    risk: {
      title: 'Диапазон на резултатите',
      intro:
        'Методът Монте Карло симулира {{paths}} случайни пътя за инфлацията и доходността (начално число {{seed}}). Всяка графика показва медианния път, диапазона, в който попадат 8 от 10 пътя (P10–P90), и диапазона за 9 от 10 пътя (P5–P95).',
      noUncertainty:
        'Нищо в този план не е несигурно, затова всички пътища съвпадат и диапазоните се сливат в една линия.',
      fanTitle: 'Нетно състояние, {{name}}: диапазон на резултатите',
      tableTitle: 'Резултати през {{year}}',
      variant: 'Вариант',
    },
    appendix: {
      title: 'Приложение',
      yearly: 'По години: {{name}}',
      yearlyCaption:
        'Приходите и разходите са сумирани по календарни години; наличността и нетното състояние са към края на годината.',
      cashEnd: 'Наличност в края на годината',
      netWorthEnd: 'Нетно състояние в края на годината',
    },
    method: {
      title: 'Как се изчисляват числата',
      time: 'Симулацията върви месец по месец. Салдата са към края на всеки месец, а първата вноска по кредит е в месеца след отпускането му.',
      growth:
        'Ръстът, доходността и инфлацията се натрупват месечно, така че 12 месеца при годишен процент добавят точно този процент. Сумите, въведени в днешни пари, се индексират от началния месец.',
      loans:
        'Кредитите следват банковата практика: месечната лихва е годишната, разделена на 12. При променлива лихва вноската се преизчислява.',
      real: 'Реалните стойности са номиналните, разделени на инфлацията от началото, затова показват суми в пари от началната година.',
      tax: 'Данъците следват правилата във всяко данъчно перо: осигуровки до максималния осигурителен доход, след това данък върху остатъка, с посочените там ставки и облекчения.',
      monteCarlo:
        'Монте Карло тегли по една случайна стойност за всеки фактор и година, с връзка между годините, където е зададена. Всички варианти използват едни и същи стойности, затова разликите между тях отразяват решенията, а не случайността. Перцентилите се изчисляват път по път.',
      rounding:
        'Изчисленията са с пълна точност, а сумите се закръгляват само за показване, затова сборът може леко да се различава от сбора на закръглените числа.',
    },
    sources: {
      title: 'Данъчни правила и източници',
      checked: '– стойностите са проверени спрямо официалните източници на {{date}}:',
    },
    disclaimer:
      'Този отчет е прогноза въз основа на допусканията и перата в плана. Той не е финансов, данъчен или правен съвет. Действителните резултати ще се различават, затова обсъждайте важните решения с квалифициран консултант.',
  },
  help: {
    menu: 'Помощ',
    title: 'Помощ за Kasora',
    tabs: { welcome: 'Добре дошли', tutorial: 'Ръководство', shortcuts: 'Клавиши' },
    menuItems: {
      welcome: 'Какво е Kasora?',
      tutorial: 'Ръководство',
      shortcuts: 'Клавишни комбинации',
      tour: 'Кратко запознаване',
    },
    welcome: {
      eyebrow: 'Симулатор на лични финанси',
      headline: 'Вижте накъде вървят парите ви',
      lead: 'Kasora разиграва плана ви месец по месец: приходи, разходи, спестявания, кредити и данъци, с отчетена инфлация. Сравнете решения като наем или покупка, преди да ги вземете, и вижте колко сигурни можете да бъдете.',
      whyTitle: 'Защо Kasora',
      features: {
        monthly: {
          title: 'Всеки месец, десетилетия напред',
          body: 'Повишения на заплатата, наем, ипотеки, инвестиции и данъци, симулирани месец по месец до 60 години напред – в днешни пари или такива, каквито ще бъдат.',
        },
        compare: {
          title: 'Сравнявайте решения едно до друго',
          body: 'Направете вариант на плана, променете едно нещо и вижте разликата: докъде стига всеки вариант и кога единият излиза напред окончателно.',
        },
        uncertainty: {
          title: 'Диапазон, а не едно предположение',
          body: 'Монте Карло пресмята хиляди възможни бъдещета за инфлацията и доходността и показва диапазона на резултатите и шанса да постигнете целите си.',
        },
        explain: {
          title: 'Всяко число е обяснено',
          body: 'Щракнете върху който и да е месец, за да видите всяко движение на парите в него. Регистърът и таблиците показват останалото – нищо не е скрито.',
        },
        private: {
          title: 'Поверителност по замисъл',
          body: 'Всичко се изчислява във вашия браузър. Не ви е нужен профил – влезте само ако искате синхронизация между устройства.',
        },
        share: {
          title: 'Готово за споделяне',
          body: 'Експортирайте прегледен отчет в PDF или изпратете връзка само за преглед на партньор или консултант.',
        },
      },
      differentTitle: 'Какво го отличава',
      different: {
        fair: {
          lead: 'Честни сравнения',
          body: 'Всички варианти минават през едно и също случайно бъдеще, затова разликите идват от решенията ви, а не от късмета.',
        },
        real: {
          lead: 'Реални стойности',
          body: 'Сумите, коригирани с инфлацията, показват какво наистина ще можете да купите.',
        },
        portable: {
          lead: 'Планът е ваш',
          body: 'Експортирайте плана като файл или отчет в PDF по всяко време – нищо не ви обвързва.',
        },
        keyboard: {
          lead: 'Бързо и достъпно',
          body: 'Всичко работи от клавиатурата и с екранни четци.',
        },
      },
      tour: 'Кратко запознаване',
      tourHint: 'Три кратки спирки: вариантите, графиките и командната палитра.',
      tutorial: 'Прочетете ръководството',
      explore: 'Към приложението',
    },
    tutorial: {
      steps: 'Стъпки на ръководството',
      stepOf: 'Стъпка {{n}} от {{total}}',
      back: 'Назад',
      next: 'Напред',
      done: 'Готово',
      tryIt: 'Опитайте',
      projects: {
        title: 'Проекти и варианти',
        p1: 'Проектът е един план: кога започва, колко години напред гледа, началната ви наличност и очакваната инфлация.',
        p2: 'Вариантите са версии на плана, например „Наем“ и „Покупка“. Общите пера, като заплатата ви, са споделени, така че ги въвеждате само веднъж.',
      },
      items: {
        title: 'Въведете какво влиза и излиза',
        p1: 'Перата са частите на плана: приходи, разходи, еднократни събития, спестявания и инвестиции, кредити и правила като данък или „спестявай 20 % от дохода“.',
        p2: 'Изберете перо, за да го редактирате в панела вдясно. Сумите могат да следват инфлацията, да растат с даден процент или да се повишават на стъпки.',
      },
      assumptions: {
        title: 'Задайте допусканията',
        p1: 'Допусканията важат за всички варианти: началният месец, хоризонтът, началната наличност, валутата и инфлацията.',
        p2: 'Всяка ставка може да се променя във времето – например ипотека с фиксирана лихва за пет години, а след това инфлация плюс 2 %. Изберете „Промяна във времето“ до полето.',
      },
      charts: {
        title: 'Четете графиките',
        p1: 'Изгледите показват нетното състояние, паричния поток, активите и задълженията и изплащането на дълговете – в номинални суми или в реални, в днешни пари.',
        p2: 'Щракнете върху месец или натиснете Enter върху избрана графика, за да видите какво точно се е случило в него. Всяка графика може да се види и като таблица.',
      },
      compare: {
        title: 'Сравнявайте и планирайте при несигурност',
        p1: 'Сравнението показва как се представя всеки вариант спрямо базовия: разликата във времето и месеца, от който единият излиза напред окончателно.',
        p2: 'Включете Монте Карло, за да видите диапазон от резултати вместо един път, риска от недостиг на наличност и шанса да постигнете целите си.',
      },
      share: {
        title: 'Запазване, споделяне и отчети',
        p1: 'Работата ви се запазва автоматично в този браузър. Влезте, за да я синхронизирате между устройствата си – нищо друго не се променя.',
        p2: 'Споделете връзка само за преглед или експортирайте отчет в PDF с графиките, таблиците и методиката зад числата.',
      },
    },
    shortcuts: { intro: 'Натиснете ? навсякъде, за да отворите този списък.' },
  },
  common: {
    close: 'Затвори',
    comingSoon: 'Очаквайте скоро',
    cancel: 'Отказ',
    delete: 'Изтриване',
    undo: 'Отмяна',
    more: 'Още',
    none: 'Няма',
  },
};
